import React, { useState, useEffect, FunctionComponent, useRef, useCallback, useMemo } from 'react'
import { Range } from 'react-date-range'

// Components
import GlobalStyles, { MultiSelect } from '../../../../components/component-items/styles'
import { TableCell } from '../../../../components/component-items/datatable'
import { Form, InputGroup } from 'react-bootstrap'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import { toast } from 'react-toastify'
import { CustomDateInput } from '../../../../components/component-items/custom-date-input'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt, faLayerPlus, faCalendarWeek } from '@fortawesome/pro-duotone-svg-icons'

// Types
import {
  FiltersProps,
  defaultFilters,
  SendNotificationType,
  userList,
  productVariantListOptions,
  productVariantHeaderOption,
  OrderFieldTypes,
  AddressFieldTypes,
  ASNFieldTypes,
  ReturnFieldTypes,
  InventoryFieldTypes,
  ProductVariantFieldTypes,
  BillingFieldTypes,
  OrderActionOptions,
  ASNActionOptions,
  ReturnActionOptions,
  InventoryActionOptions,
  ProductVariantActionOptions,
  BillingActionOptions,
} from './types'
import { Calendar } from 'react-date-range'

import styled from 'styled-components'
import { useClick } from '../../../../components/component-items/helpers'
import MentionComponent from '../../../../components/component-items/mentions'

export const DateRangeStyled = styled.div`
  position: absolute;
  bottom: 32px;
  right: -18px;
  z-index: 10;
  overflow: scroll;
  background-color: white;
  box-shadow: 0px 0px 13px 0px rgba(82, 63, 105, 0.19);
  margin: 2em;
  width: fit-content;
`

type ActionsProps = {
  automationType: string
  filters: FiltersProps
  event: any
  actions: any
  setActions: (actions: any) => void
  children?: React.ReactNode
  r: number
  g: number
  b: number
  initialProductVariantOptions?: any[]
}
export const Actions: FunctionComponent<ActionsProps> = ({
  automationType,
  filters,
  event,
  actions,
  setActions,
  children,
  r,
  g,
  b,
  initialProductVariantOptions = [],
}) => {
  const [showCalendar, setShowCalendar] = useState(false)
  const ref = useRef<HTMLLIElement | null>(null)
  useClick(ref, setShowCalendar)

  const mentionRef = useRef<any | null>(null)
  const [productVariantOptions, setProductVariantOptions] = useState<any[]>(initialProductVariantOptions)
  const [userOptions, setUserOptions] = useState<any[]>([])
  const hydratedVariantIdsRef = useRef<Set<string>>(new Set())
  const editItemActionTypes = ['edit_order_item', 'edit_asn_item', 'edit_return_item']

  // Create a proper default action object (not a filter)
  const defaultAction = {
    type: '',
    result: {
      fieldType: '',
      fieldOperator: '',
      fieldValue: '',
      notificationTo: '',
    },
    logical: [],
  }

  const loadProductVariantOptions = useCallback(async (inputValue: string) => {
    const options = await productVariantListOptions(inputValue)
    setProductVariantOptions(options)
    return options
  }, [])

  useEffect(() => {
    if (initialProductVariantOptions?.length) {
      setProductVariantOptions((currentOptions) => {
        if (currentOptions.length) {
          return currentOptions
        }
        return initialProductVariantOptions
      })
    }
  }, [initialProductVariantOptions])

  const loadUserOptions = useCallback(async (inputValue: string) => {
    const options = await userList(inputValue)
    setUserOptions(options)
    return options
  }, [])
  const selectedProductVariant = useMemo(() => {
    const fieldTypeValue = actions?.result?.fieldType
    if (!fieldTypeValue) {
      return null
    }
    if (typeof fieldTypeValue === 'object') {
      return fieldTypeValue
    }
    const normalizedFieldTypeValue = `${fieldTypeValue}`
    return productVariantOptions.find(({ value }) => `${value}` === normalizedFieldTypeValue) ?? null
  }, [actions?.result?.fieldType, productVariantOptions])

  useEffect(() => {
    const isEditItemAction = editItemActionTypes.includes(actions?.type)
    if (!isEditItemAction) {
      return
    }

    const fieldTypeValue = actions?.result?.fieldType
    if (!fieldTypeValue || typeof fieldTypeValue === 'object' || selectedProductVariant) {
      return
    }

    const normalizedVariantId = `${fieldTypeValue}`
    if (hydratedVariantIdsRef.current.has(normalizedVariantId)) {
      return
    }

    const hydrateSelectedVariant = async () => {
      hydratedVariantIdsRef.current.add(normalizedVariantId)

      try {
        const option = await productVariantHeaderOption(normalizedVariantId)
        if (!option) {
          hydratedVariantIdsRef.current.delete(normalizedVariantId)
          return
        }

        setProductVariantOptions((currentOptions) => {
          const alreadyExists = currentOptions.some(({ value }) => `${value}` === `${option.value}`)
          if (alreadyExists) {
            return currentOptions
          }
          return [option, ...currentOptions]
        })
      } catch (_error) {
        hydratedVariantIdsRef.current.delete(normalizedVariantId)
      }
    }

    hydrateSelectedVariant()
  }, [actions?.type, actions?.result?.fieldType, selectedProductVariant])

  const selectedUsers = useMemo(() => {
    const notificationTo = actions?.result?.notificationTo
    if (!notificationTo) {
      return []
    }

    const optionFromValue = (value: any) => {
      if (typeof value === 'object') {
        return value
      }
      const found = userOptions.find(({ value: optionValue }) => optionValue === value)
      if (found) {
        return found
      }
      return { label: value, value }
    }

    const values = Array.isArray(notificationTo) ? notificationTo : [notificationTo]
    return values.map(optionFromValue).filter(Boolean)
  }, [actions?.result?.notificationTo, userOptions])

  const [logical, setLogical] = useState(actions.logical || [])
  useEffect(() => {
    setActions({ ...actions, logical: logical })
  }, [logical])

  function addRow() {
    setLogical((logical: FiltersProps[]) => [...logical, defaultAction])
  }

  const setType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'logical_not') {
      setLogical([defaultAction])
    } else {
      setLogical([defaultAction, defaultAction])
    }
    setActions({ ...defaultAction, type: e.target.value })
  }

  const isLogical = ['logical_and', 'logical_or', 'logical_not'].includes(actions.type)
  const isEditItem = editItemActionTypes.includes(actions.type)

  // Instantiate Field Types
  const orderFieldList = OrderFieldTypes()
  const addressFieldList = AddressFieldTypes()
  const asnFieldList = ASNFieldTypes()
  const returnFieldList = ReturnFieldTypes()
  const inventoryFieldList = InventoryFieldTypes()
  const productVariantFieldList = ProductVariantFieldTypes()
  const billingFieldList = BillingFieldTypes()

  let fieldList: any = []
  if (automationType === 'order') {
    if (actions.type === 'edit_order_field') {
      fieldList = [...orderFieldList]
    } else if (actions.type === 'edit_address_field') {
      fieldList = [...addressFieldList]
    }
  } else if (automationType === 'asn') {
    if (actions.type === 'edit_asn_field') {
      fieldList = [...asnFieldList]
    }
  } else if (automationType === 'return') {
    if (actions.type === 'edit_return_field') {
      fieldList = [...returnFieldList]
    }
  } else if (automationType === 'inventory') {
    if (actions.type === 'edit_inventory_field') {
      fieldList = [...inventoryFieldList]
    }
  } else if (automationType === 'product_variant') {
    if (actions.type === 'edit_product_variant_field') {
      fieldList = [...productVariantFieldList]
    }
  } else if (automationType === 'billing') {
    if (actions.type === 'edit_billing_field') {
      fieldList = [...billingFieldList]
    }
  }

  const normalizeBooleanValue = (value: any) => {
    if (value === true || value === false) return value
    if (value === undefined || value === null || value === '') return null
    const normalized = `${value}`.trim().toLowerCase()
    if (['true', '1', 'yes'].includes(normalized)) return true
    if (['false', '0', 'no'].includes(normalized)) return false
    return null
  }

  // Ensure actions.result exists
  if (!actions.result) {
    actions.result = defaultAction.result
  }

  const fieldOperator: any = fieldList?.find((t: any) => t.value === actions.result?.fieldType)
  const fieldOptions: any = fieldOperator?.options
  const fieldType: any = fieldOperator?.type
  const normalizedBooleanValue = normalizeBooleanValue(actions.result?.fieldValue)
  const isFieldOptionsArray = Array.isArray(fieldOptions)
  const selectValue = isFieldOptionsArray
    ? Array.isArray(actions.result?.fieldValue)
      ? fieldOptions.filter((option: any) =>
          actions.result.fieldValue.some((value: any) => `${value}` === `${option.value}`)
        )
      : fieldOptions.find((option: any) => `${option.value}` === `${actions.result?.fieldValue}`) ||
        (actions.result?.fieldValue ? { label: actions.result.fieldValue, value: actions.result.fieldValue } : null)
    : null

  const [asyncFieldOptions, setAsyncFieldOptions]: any = useState([])
  const asyncSelectValue =
    asyncFieldOptions.find(({ value }: any) => `${value}` === `${actions.result?.fieldValue}`) ||
    (actions.result?.fieldValue ? { label: actions.result.fieldValue, value: actions.result.fieldValue } : null)

  useEffect(() => {
    // Load async options and set them when they are ready
    const loadOptionsAsync = async () => {
      const options = await fieldOptions() // Assuming fieldOptions is a function that fetches options
      setAsyncFieldOptions(options)
    }

    if (fieldOperator?.async) {
      loadOptionsAsync()
    }
  }, [fieldOperator, fieldOptions, actions.result?.fieldValue])

  useEffect(() => {
    const hasValue =
      actions.result?.fieldValue !== undefined &&
      actions.result?.fieldValue !== null &&
      actions.result?.fieldValue !== ''
    let fieldValue: any = actions.result?.fieldValue

    if (actions.type === 'send_notification') {
      if (!hasValue) {
        fieldValue = ''
      }
      // keep a reference for saving, but do not overwrite the message text
      if (!actions.result.messageRef) {
        setActions({
          ...actions,
          result: { ...actions.result, messageRef: mentionRef, fieldValue },
        })
        return
      }
    } else if (fieldType === 'date') {
      if (!hasValue) {
        fieldValue = new Date()
      }
    } else if (fieldType === 'boolean') {
      const normalizedValue = normalizeBooleanValue(fieldValue)
      fieldValue = normalizedValue !== null ? normalizedValue : false
    } else if (!hasValue) {
      fieldValue = ''
    }
    if (fieldValue !== actions.result?.fieldValue) {
      setActions({
        ...actions,
        result: { ...actions.result, fieldValue: fieldValue },
      })
    }
  }, [fieldType, actions.type])

  // Helper function to parse DD/MM/YYYY format
  const parseDateFromBackend = (dateStr: string | Date): Date | null => {
    if (!dateStr) return null

    // If already a Date object, return it
    if (dateStr instanceof Date) {
      return isNaN(dateStr.getTime()) ? null : dateStr
    }

    // Handle canonical YYYY-MM-DD format as local date (avoid UTC shift)
    const yyyymmddMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (yyyymmddMatch) {
      const [, year, month, day] = yyyymmddMatch
      return new Date(Number(year), Number(month) - 1, Number(day))
    }

    // Handle DD/MM/YYYY format from backend
    const parts = String(dateStr).split('/')
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1 // JS months are 0-indexed
      const year = parseInt(parts[2], 10)

      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return new Date(year, month, day)
      }
    }

    // Fallback to native Date parsing
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? null : date
  }

  const handleDateChange = (newDate: Date | Range | null) => {
    if (newDate instanceof Date) {
      const day = String(newDate.getDate()).padStart(2, '0')
      const month = String(newDate.getMonth() + 1).padStart(2, '0')
      const year = newDate.getFullYear()
      const formattedDate = `${year}-${month}-${day}`

      setActions({
        ...actions,
        result: { ...actions.result, fieldValue: formattedDate },
      })
    }
  }

  return (
    <>
      <GlobalStyles.DataTable>
        <tbody>
          <tr style={{ border: 'unset' }}>
            <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
              <Form.Select required value={actions.type} onChange={(e) => setType(e)}>
                <option value={''} hidden>
                  - Field -
                </option>
                {automationType === 'order' ? (
                  <OrderActionOptions {...{ event }} />
                ) : automationType === 'asn' ? (
                  <ASNActionOptions {...{ event }} />
                ) : automationType === 'return' ? (
                  <ReturnActionOptions {...{ event }} />
                ) : automationType === 'inventory' ? (
                  <InventoryActionOptions {...{ event }} />
                ) : automationType === 'product_variant' ? (
                  <ProductVariantActionOptions {...{ event }} />
                ) : automationType === 'billing' ? (
                  <BillingActionOptions {...{ event }} />
                ) : null}
              </Form.Select>
              {isLogical ? (
                <GlobalStyles.DataTable>
                  <tbody>
                    <tr style={{ border: 'unset' }}>
                      <TableCell style={{ background: `rgb(${r}, ${g}, ${b})`, border: '1px solid #e1eaf1' }}>
                        {logical.map((newFilters: FiltersProps, index: number) => (
                          <React.Fragment key={index}>
                            <Actions
                              automationType={automationType}
                              filters={filters}
                              event={event}
                              actions={newFilters}
                              initialProductVariantOptions={initialProductVariantOptions}
                              setActions={(e: any) =>
                                setLogical((logical: FiltersProps[]) => [
                                  ...logical.slice(0, index),
                                  { ...e },
                                  ...logical.slice(index + 1),
                                ])
                              }
                              r={r * 0.95}
                              g={g * 0.95}
                              b={b * 0.95}
                            >
                              {actions.type !== 'logical_not' ? (
                                <TableCell
                                  center
                                  style={{ verticalAlign: 'unset', width: 50, background: 'rgb(0,0,0,0)' }}
                                  input={
                                    <div className="dropdown__container" style={{ minWidth: 60 }}>
                                      {logical.length > 2 ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setLogical((logical: FiltersProps[]) =>
                                              logical.filter((c) => c !== newFilters)
                                            )
                                          }
                                        >
                                          <FontAwesomeIcon icon={faTrashAlt} />
                                        </button>
                                      ) : null}
                                      <button type="button" onClick={() => addRow()}>
                                        <FontAwesomeIcon icon={faLayerPlus} />
                                      </button>
                                    </div>
                                  }
                                />
                              ) : null}
                            </Actions>
                            {index + 1 !== logical.length ? (
                              <StatusBoxStyled
                                className={'blue'}
                                style={{ margin: '1em auto', width: 100, textAlign: 'center' }}
                              >
                                {actions.type === 'logical_and' ? 'AND' : 'OR'}
                              </StatusBoxStyled>
                            ) : null}
                          </React.Fragment>
                        ))}
                      </TableCell>
                    </tr>
                  </tbody>
                </GlobalStyles.DataTable>
              ) : null}
            </TableCell>
            {fieldList?.length > 0 ? (
              <>
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <Form.Select
                    required
                    value={actions.result.fieldType}
                    onChange={(e) =>
                      setActions({ ...actions, result: { ...actions.result, fieldType: e.target.value } })
                    }
                  >
                    <option value="" disabled hidden>
                      -- Field Type --
                    </option>
                    {fieldList
                      ?.filter((a: any) => a.actionable)
                      .map((type: any) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                  </Form.Select>
                </TableCell>
                {fieldOperator?.async ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <MultiSelect>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={true}
                        loadOptions={fieldOptions}
                        value={asyncSelectValue}
                        onChange={(e: any) =>
                          setActions({
                            ...actions,
                            result: { ...actions.result, fieldValue: e.value },
                          })
                        }
                        closeMenuOnSelect={false}
                        className="basic-multi-select"
                      />
                    </MultiSelect>
                  </TableCell>
                ) : fieldOptions?.length > 0 ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <Select
                      options={fieldOptions}
                      value={selectValue}
                      onChange={(e: any) =>
                        setActions({
                          ...actions,
                          result: { ...actions.result, fieldValue: e.value },
                        })
                      }
                    ></Select>
                  </TableCell>
                ) : fieldType === 'boolean' ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <Select
                      options={[
                        { label: 'True', value: true },
                        { label: 'False', value: false },
                      ]}
                      value={
                        normalizedBooleanValue !== false
                          ? { label: 'True', value: true }
                          : { label: 'False', value: false }
                      }
                      onChange={(e: any) =>
                        setActions({
                          ...actions,
                          result: { ...actions.result, fieldValue: e.value },
                        })
                      }
                    />
                  </TableCell>
                ) : fieldType === 'date' ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <CustomDateInput
                      value={actions.result.fieldValue ? parseDateFromBackend(actions.result.fieldValue) : null}
                      onChange={handleDateChange}
                      required={true}
                      placeholder="MM-DD-YYYY"
                    />
                  </TableCell>
                ) : (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <Form.Control
                      type="text"
                      required
                      placeholder=""
                      value={actions.result.fieldValue}
                      onChange={(e) =>
                        setActions({
                          ...actions,
                          result: { ...actions.result, fieldValue: e.target.value },
                        })
                      }
                    />
                  </TableCell>
                )}
              </>
            ) : isEditItem ? (
              <>
                <TableCell style={{ padding: '5px 5px' }}>
                  <MultiSelect>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={productVariantOptions.length ? productVariantOptions : true}
                      placeholder="SKU"
                      loadOptions={loadProductVariantOptions}
                      value={selectedProductVariant}
                      onChange={(e) => setActions({ ...actions, result: { ...actions.result, fieldType: e } })}
                      closeMenuOnSelect={false}
                      className="basic-multi-select"
                    />
                  </MultiSelect>
                </TableCell>
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <Form.Control
                    required
                    type="number"
                    placeholder="quantity"
                    value={actions.result.fieldValue}
                    onChange={(e) =>
                      setActions({ ...actions, result: { ...actions.result, fieldValue: e.target.value } })
                    }
                  />
                </TableCell>
              </>
            ) : actions.type === 'send_notification' ? (
              <>
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <Form.Select
                    required
                    value={actions.result.fieldType}
                    onChange={(e) =>
                      setActions({
                        ...actions,
                        result: { ...actions.result, fieldType: e.target.value, messageRef: mentionRef },
                      })
                    }
                  >
                    <SendNotificationType automationType={automationType} />
                  </Form.Select>
                </TableCell>
                {actions.result.fieldType === 'email' ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <Form.Control
                      required
                      type="text"
                      placeholder="example@email.co, "
                      value={actions.result.notificationTo}
                      onChange={(e) =>
                        setActions({ ...actions, result: { ...actions.result, notificationTo: e.target.value } })
                      }
                    />
                  </TableCell>
                ) : actions.result.fieldType === 'sms' ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <Form.Control
                      required
                      type="text"
                      placeholder="1234567890, "
                      value={actions.result.notificationTo}
                      onChange={(e) =>
                        setActions({ ...actions, result: { ...actions.result, notificationTo: e.target.value } })
                      }
                    />
                  </TableCell>
                ) : actions.result.fieldType === 'slack' ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <Form.Control
                      required
                      type="text"
                      placeholder="slack-channel, "
                      value={actions.result.notificationTo}
                      onChange={(e) =>
                        setActions({ ...actions, result: { ...actions.result, notificationTo: e.target.value } })
                      }
                    />
                  </TableCell>
                ) : actions.result.fieldType === 'app' ? (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <MultiSelect style={{ minWidth: 200 }}>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={userOptions.length ? userOptions : true}
                        placeholder="Users"
                        loadOptions={loadUserOptions}
                        value={selectedUsers}
                        onChange={(e) => setActions({ ...actions, result: { ...actions.result, notificationTo: e } })}
                        closeMenuOnSelect={false}
                        className="basic-multi-select"
                        isMulti={true}
                      />
                    </MultiSelect>
                  </TableCell>
                ) : null}
                {actions.type !== 'send_notification' && (
                  <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                    <Form.Control
                      required
                      type="text"
                      placeholder=""
                      value={actions.result.fieldValue}
                      onChange={(e) =>
                        setActions({ ...actions, result: { ...actions.result, fieldValue: e.target.value } })
                      }
                    />
                  </TableCell>
                )}
              </>
            ) : null}
            {children}
          </tr>
          {actions.type === 'send_notification' && (
            <tr>
              <TableCell style={{ background: 'rgb(0,0,0,0)' }} colspan={5}>
                <MentionComponent
                  ref={mentionRef}
                  value={actions.result.fieldValue}
                  onTextChange={(text: string) =>
                    setActions((prev: any) => ({
                      ...prev,
                      result: { ...prev.result, fieldValue: text, messageRef: mentionRef },
                    }))
                  }
                />
              </TableCell>
            </tr>
          )}
        </tbody>
      </GlobalStyles.DataTable>
    </>
  )
}
